import { motion } from "framer-motion";

const points = [
    "Residential Apartments",
    "Independent Villas",
    "Commercial Buildings",
    "Property Development",
    "Real Estate Promotion",
    "Turnkey Construction",
];

const imgs = {
    hero: "https://images.pexels.com/photos/16110999/pexels-photo-16110999.jpeg",
    warm: "https://images.pexels.com/photos/13722826/pexels-photo-13722826.jpeg",
    lounge: "https://images.pexels.com/photos/20418771/pexels-photo-20418771.jpeg",
    exterior: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1600&q=80",
};

const rev = {
    hidden: { opacity: 0, y: 30 },
    show: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.8, delay: i * 0.08 },
    }),
};

export default function About() {
    return (
        <section
            id="about"
            className="relative bg-[#0b0b0b] py-24 md:py-40 px-6 md:px-10"
            data-testid="about-section"
        >
            <div className="mx-auto max-w-[1440px]">
                {/* Header */}
                <motion.div
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: "-15%" }}
                    variants={rev}
                    className="flex items-center gap-6 mb-10"
                >
                    <span className="w-16 h-px bg-[#C89B3C]" />
                    <span className="text-[10px] uppercase tracking-[0.4em] text-[#C89B3C]">
                        Chapter 05 — The Manifesto
                    </span>
                </motion.div>

                <div className="grid grid-cols-12 gap-6 items-end mb-12 md:mb-20">
                    <motion.h2
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true, margin: "-15%" }}
                        variants={rev}
                        custom={1}
                        className="col-span-12 lg:col-span-8 font-display font-light text-white text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight"
                    >
                        Building More
                        <br />
                        Than <em className="text-[#E2C56D]">Structures.</em>
                    </motion.h2>
                    <motion.p
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true, margin: "-15%" }}
                        variants={rev}
                        custom={2}
                        className="col-span-12 lg:col-span-4 text-[#bebebe] text-base md:text-lg leading-relaxed font-light"
                    >
                        For over four decades, BRA Sheriff has been drawing, pouring and
                        delivering spaces where structural excellence meets timeless
                        aesthetics.
                    </motion.p>
                </div>

                {/* NEW editorial bento layout — 3 image tiles + a description tile + a spec list tile */}
                <div className="grid grid-cols-12 grid-flow-dense gap-3 md:gap-4">
                    {/* Wide hero image with numbered plate */}
                    <motion.figure
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-10%" }}
                        transition={{ duration: 0.9 }}
                        className="col-span-12 md:col-span-8 relative overflow-hidden aspect-[16/10] group"
                    >
                        <img
                            src={imgs.hero}
                            alt="Contemporary residence"
                            className="w-full h-full object-cover transition-transform duration-[1600ms] group-hover:scale-[1.06]"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0b0b0b] via-transparent to-transparent opacity-80" />
                        <div className="absolute inset-0 border border-[rgba(212,175,55,0.35)]" />

                        {/* Plate overlay */}
                        <div className="absolute left-6 top-6 md:left-8 md:top-8 flex items-center gap-4">
                            <span className="font-display text-4xl md:text-5xl text-[#E2C56D] leading-none">
                                №1
                            </span>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.35em] text-[#C89B3C]">
                                    Residence
                                </div>
                                <div className="text-white/95 text-sm md:text-base font-light">
                                    Contemporary Apartment · Bangalore
                                </div>
                            </div>
                        </div>

                        {/* Bottom caption */}
                        <div className="absolute left-6 right-6 md:left-8 md:right-8 bottom-6 md:bottom-8 flex items-end justify-between gap-4">
                            <div className="font-serif-alt text-white text-xl md:text-3xl leading-tight max-w-md">
                                Every square foot a signature.
                            </div>
                            <span className="hidden md:inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.35em] text-white/85">
                                <span className="w-10 h-px bg-[#C89B3C]" /> Portfolio
                            </span>
                        </div>
                    </motion.figure>

                    {/* Description tile */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-10%" }}
                        transition={{ duration: 0.9, delay: 0.12 }}
                        className="col-span-12 md:col-span-4 border border-[rgba(212,175,55,0.25)] bg-[#111111]/70 backdrop-blur-xl p-8 md:p-9 flex flex-col justify-between min-h-[220px]"
                    >
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.35em] text-[#C89B3C] mb-4">
                                Our Philosophy
                            </div>
                            <p className="font-serif-alt text-white text-xl md:text-2xl leading-snug">
                                Thoughtfully planned spaces that combine{" "}
                                <em className="text-[#E2C56D] not-italic">structural
                                excellence</em>{" "}
                                with timeless aesthetics.
                            </p>
                        </div>
                        <div className="mt-8 flex items-center gap-3 text-[10px] uppercase tracking-[0.35em] text-[#7a7a7a]">
                            <span className="w-8 h-px bg-[#C89B3C]" /> Family-run since 1924
                        </div>
                    </motion.div>

                    {/* Tall portrait image */}
                    <motion.figure
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-10%" }}
                        transition={{ duration: 0.9, delay: 0.06 }}
                        className="col-span-6 md:col-span-4 relative overflow-hidden aspect-[3/4] group"
                    >
                        <img
                            src={imgs.warm}
                            alt="Warm interior"
                            className="w-full h-full object-cover transition-transform duration-[1600ms] group-hover:scale-105"
                        />
                        <div className="absolute inset-0 border border-[rgba(212,175,55,0.35)]" />
                        <div className="absolute left-4 top-4 text-[10px] uppercase tracking-[0.35em] text-white/90">
                            №2
                        </div>
                        <div className="absolute left-4 right-4 bottom-4 text-[10px] uppercase tracking-[0.3em] text-white/90">
                            Interiors · Materials
                        </div>
                    </motion.figure>

                    {/* Spec list tile */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-10%" }}
                        transition={{ duration: 0.9, delay: 0.12 }}
                        className="col-span-12 md:col-span-4 border border-[rgba(212,175,55,0.25)] p-8 md:p-9 relative"
                    >
                        <div className="text-[10px] uppercase tracking-[0.35em] text-[#C89B3C] mb-6">
                            What we specialise in
                        </div>
                        <ul>
                            {points.map((p, i) => (
                                <motion.li
                                    key={p}
                                    initial={{ opacity: 0, x: -8 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true, margin: "-15%" }}
                                    transition={{ duration: 0.5, delay: i * 0.05 }}
                                    className="group flex items-center gap-4 py-2.5 border-b border-[rgba(212,175,55,0.15)] last:border-b-0"
                                    data-testid={`about-spec-${i}`}
                                >
                                    <span className="font-display text-[#C89B3C] text-xs">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span className="text-white/95 text-sm md:text-base font-light group-hover:text-[#E2C56D] transition-colors">
                                        {p}
                                    </span>
                                </motion.li>
                            ))}
                        </ul>
                    </motion.div>

                    {/* Wide landscape image */}
                    <motion.figure
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-10%" }}
                        transition={{ duration: 0.9, delay: 0.18 }}
                        className="col-span-6 md:col-span-4 relative overflow-hidden aspect-[3/4] group"
                    >
                        <img
                            src={imgs.lounge}
                            alt="Lavish lounge"
                            className="w-full h-full object-cover transition-transform duration-[1600ms] group-hover:scale-105 grayscale-[35%]"
                        />
                        <div className="absolute inset-0 border border-[rgba(212,175,55,0.35)]" />
                        <div className="absolute left-4 top-4 text-[10px] uppercase tracking-[0.35em] text-white/90">
                            №3
                        </div>
                        <div className="absolute left-4 right-4 bottom-4 text-[10px] uppercase tracking-[0.3em] text-white/90">
                            Craftsmanship
                        </div>
                    </motion.figure>
                </div>
            </div>
        </section>
    );
}
